/**
 * ChatHeader — top bar of the open chat (Iter 138, Feb 2026).
 *
 * Extracted from ChatsPage.jsx as the next phase of the scalability split
 * after iter 135's ChatListSidebar.
 *
 * Owns the visible header strip: avatar, name + sentiment, country/category
 * pills, response timer, status badges, action buttons (refresh / call /
 * label / payment link / email ticket / AI toggle / mobile kebab).
 *
 * State stays in ChatsPage. ~30 props is verbose but explicit. The mobile
 * kebab is a popover whose `showMobileHeaderMenu` boolean is also hoisted —
 * keeping it in sync with the existing close-on-outside-click effect.
 *
 * Note: drag-and-drop handlers and modals (CreateTicket, Payment, Label
 * picker, Customer 360, Notify-team, Mobile header menu) live OUTSIDE this
 * component — ChatHeader only triggers them via setState callbacks.
 */

import {
  AlertTriangle, ArrowLeft, CheckCircle, Clock, FileDown, IndianRupee, Mail,
  MailCheck, MailOpen, MoreVertical, RefreshCw, Send, Share2, Sparkles, Tag,
  User, UserSearch, XCircle, X,
} from "lucide-react";
import ChatHeaderCallButton from "./ChatHeaderCallButton";
import { CategoryBadge } from "./ChatRightPanel";
import { detectPhoneCountry } from "../../lib/phoneCountry";
import { getResponseTimer } from "./utils";
import { toast } from "sonner";

export default function ChatHeader({
  user,
  selectedChat,
  selectedLead,
  agents,
  messages,
  showMobileHeaderMenu,
  showDetails,
  // openers / setters
  setSelectedChat,
  setShowMobileHeaderMenu,
  setShowDetails,
  setShowLabelPicker,
  setShowCreateTicket,
  setShowPaymentModal,
  setShowNotifyTeamModal,
  setShow360Phone,
  setQrBase64,
  setGeneratedLink,
  setPayCustomerName,
  setPayCustomerEmail,
  setPayCustomerPhone,
  setPaymentAmount,
  setPaymentCurrency,
  setPaymentDesc,
  setTicketSubject,
  setTicketDescription,
  setTicketInternal,
  setTicketEmailOverride,
  // handlers
  handleRefreshChat,
  handleAiReplyOnce,
  handleAiDeactivate,
  handleChatAction,
  // iter 153.43 — export / share
  setShowExportModal,
  setShowShareModal,
  // helpers from parent
  sentimentIcon,
}) {
  return (
              <div className="flex items-center gap-3 px-5 py-3 bg-white border-b border-[--border] flex-shrink-0">
                <button className="md:hidden btn-icon" onClick={() => setSelectedChat(null)} data-testid="back-to-list-btn"><ArrowLeft size={18} /></button>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ background: "linear-gradient(135deg, #E11D48, #EA580C)", boxShadow: "0 2px 8px rgba(225,29,72,0.2)" }}>{selectedChat.name?.charAt(0)?.toUpperCase() || "?"}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-bold text-[14px] text-[--text-primary] truncate" style={{ fontFamily: "Outfit, sans-serif" }}>{selectedChat.name}</p>
                    {selectedLead?.sentiment && sentimentIcon(selectedLead.sentiment)}
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const c = detectPhoneCountry(selectedChat.phone);
                      if (!c) return null;
                      const ringColor = c.code === "IN" ? "border-orange-200 bg-orange-50" : c.code === "??" ? "border-stone-200 bg-stone-50" : "border-indigo-200 bg-indigo-50";
                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${ringColor}`}
                          title={`Detected country: ${c.label} (${c.code})`}
                          data-testid="chat-header-country-badge"
                        >
                          <span style={{ fontSize: "12px", lineHeight: 1 }}>{c.flag}</span>
                          <span className="text-[--text-secondary] tracking-wide">{c.code}</span>
                        </span>
                      );
                    })()}
                    <span className="text-[11px] text-[--text-muted]">{selectedChat.phone}</span>
                    {selectedChat.category && <CategoryBadge cat={selectedChat.category} />}
                    {(() => {
                      // Iter 152.22 (May 4, 2026, Yash P0) — WATI status is
                      // the ONLY source of truth. User directive: "we will
                      // just go by WATI statuses only and not rely on CRMs
                      // status of chats." Legacy `status` field is ignored
                      // here to prevent drift between CRM and WATI.
                      // Iter 152.22k (May 5, 2026, Yash) — ALWAYS show a
                      // status pill (was: OPEN rendered nothing). User
                      // reported tickets appearing "blank" with no status
                      // visible; that's now impossible.
                      // Iter 153 (Feb 6, 2026, Yash) — CRM is now the
                      // source of truth. PENDING dropped from the enum
                      // (legacy data with PENDING is shown as OPEN).
                      const wati = (selectedLead?.wati_status || "OPEN").toUpperCase();
                      if (wati === "SOLVED") {
                        return <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium border border-gray-200" data-testid="chat-header-status-pill" data-status="solved">Solved</span>;
                      }
                      if (wati === "EXPIRED") {
                        return <span className="text-[9px] px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded-full font-medium border border-orange-200" data-testid="chat-header-status-pill" data-status="expired">Expired</span>;
                      }
                      // Default: OPEN (and legacy PENDING) — show an explicit green pill so the
                      // header is NEVER blank.
                      return <span className="text-[9px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-medium border border-emerald-200" data-testid="chat-header-status-pill" data-status="open">Open</span>;
                    })()}
                  </div>
                </div>
                {/* Customer wait time indicator */}
                {(() => {
                  const timer = selectedChat && getResponseTimer(selectedChat);
                  if (!timer) return null;
                  return (
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold ${timer.bg} ${timer.color}`} data-testid="customer-wait-time">
                      <Clock size={12} />
                      <span>Customer waiting {timer.text}</span>
                    </div>
                  );
                })()}
                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => {
                      const phone = selectedChat?.phone || selectedLead?.phone;
                      if (!phone) { toast.error("No phone number on this lead"); return; }
                      setShow360Phone(phone);
                    }}
                    className="btn-icon hover:bg-purple-50 hover:text-purple-600 transition-colors"
                    title="Open Customer 360 (orders, calls, tickets, chat)"
                    data-testid="chat-header-c360-btn"
                  >
                    <UserSearch size={15} />
                  </button>
                  <ChatHeaderCallButton
                    phone={selectedChat?.phone || selectedLead?.phone}
                    customerName={selectedChat?.name || selectedLead?.name}
                    leadId={selectedLead?.id}
                  />
                  <button onClick={handleRefreshChat} className="btn-icon" title="Refresh messages" data-testid="refresh-chat-btn">
                    <RefreshCw size={15} />
                  </button>
                  {/* Mobile overflow menu — collapses everything below behind a kebab */}
                  <div className="relative md:hidden">
                    <button
                      onClick={() => setShowMobileHeaderMenu(v => !v)}
                      className={`btn-icon ${showMobileHeaderMenu ? "bg-[--accent-pale] text-[--accent]" : ""}`}
                      title="More actions"
                      data-testid="chat-mobile-more-btn"
                    >
                      <MoreVertical size={16} />
                    </button>
                    {showMobileHeaderMenu && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowMobileHeaderMenu(false)} />
                        <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl shadow-2xl border border-[--border] w-60 py-1.5 animate-scale-in" data-testid="chat-mobile-more-menu">
                          <button
                            onClick={() => { setShowMobileHeaderMenu(false); handleChatAction((selectedLead?.wati_status === "SOLVED") ? "open" : "close"); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left"
                            data-testid="m-close-chat"
                          >
                            {(selectedLead?.wati_status === "SOLVED") ? <><CheckCircle size={14} className="text-green-600" /> Reopen chat</> : <><XCircle size={14} className="text-red-600" /> Solved</>}
                          </button>
                          <button
                            onClick={() => { setShowMobileHeaderMenu(false); handleChatAction((selectedChat?.unread_count || 0) > 0 ? "mark-read" : "mark-unread"); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left"
                            data-testid="m-read-chat"
                          >
                            {(selectedChat?.unread_count || 0) > 0 ? <><MailOpen size={14} className="text-blue-600" /> Mark read</> : <><MailCheck size={14} className="text-orange-600" /> Mark unread</>}
                          </button>
                          <button
                            onClick={() => { setShowMobileHeaderMenu(false); setShowLabelPicker(true); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left"
                            data-testid="m-labels"
                          >
                            <Tag size={14} className="text-green-600" /> Labels
                          </button>
                          <button
                            onClick={() => { setShowMobileHeaderMenu(false); setShowPaymentModal(true); setGeneratedLink(null); setPaymentAmount(""); setPaymentDesc(""); setPaymentCurrency("INR"); setPayCustomerName(selectedLead?.customer_name || selectedLead?.name || ""); setPayCustomerPhone(selectedLead?.customer_phone || selectedLead?.phone || ""); setPayCustomerEmail(selectedLead?.customer_email || ""); setQrBase64(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left"
                            data-testid="m-payment"
                          >
                            <IndianRupee size={14} className="text-orange-600" /> Payment link
                          </button>
                          <button
                            onClick={() => {
                              setShowMobileHeaderMenu(false);
                              const lastCustomerMsg = [...(messages || [])].reverse().find((m) => m.direction === "incoming");
                              setTicketSubject(`WhatsApp chat with ${selectedLead?.name || selectedLead?.phone}`);
                              const recent = (messages || []).slice(-6).map((m) => `[${m.direction === "outgoing" ? "Us" : "Customer"}] ${m.content || "(media)"}`).join("\n");
                              setTicketDescription(lastCustomerMsg
                                ? `Escalated from WhatsApp. Recent conversation:\n\n${recent}\n\n--- Customer phone: +${selectedLead?.phone} ---`
                                : `Follow-up from WhatsApp chat with ${selectedLead?.name}. +${selectedLead?.phone}`);
                              setTicketInternal(true);
                              setTicketEmailOverride("help@exoticindia.com");
                              setShowCreateTicket(true);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left"
                            data-testid="m-email-ticket"
                          >
                            <Mail size={14} className="text-amber-600" /> Email ticket
                          </button>
                          {/* Escalate / Re-route / Detect-sentiment removed
                              from the mobile kebab too (Apr 28 2026). */}
                          <button
                            onClick={() => { setShowMobileHeaderMenu(false); selectedLead?.ai_active ? handleAiDeactivate() : handleAiReplyOnce(); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left"
                            data-testid="m-start-ai"
                            title={selectedLead?.ai_active ? "Turn off AI for this chat" : "Turn on AI for this chat"}
                          >
                            {selectedLead?.ai_active
                              ? <><X size={14} className="text-red-600" /> Stop AI on this chat</>
                              : <><Sparkles size={14} className="text-purple-600" /> Start AI on this chat</>
                            }
                          </button>
                          {/* Detect-sentiment removed from mobile menu too. */}
                          <button
                            onClick={() => { setShowMobileHeaderMenu(false); setShowExportModal && setShowExportModal(true); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left"
                            data-testid="m-export-chat"
                          >
                            <FileDown size={14} className="text-slate-700" /> Export chat
                          </button>
                          <button
                            onClick={() => { setShowMobileHeaderMenu(false); setShowShareModal && setShowShareModal(true); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left"
                            data-testid="m-share-chat"
                          >
                            <Share2 size={14} className="text-blue-600" /> Share chat
                          </button>
                          <button
                            onClick={() => { setShowMobileHeaderMenu(false); setShowDetails(true); }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] font-semibold hover:bg-gray-50 text-left border-t border-[--border]"
                            data-testid="m-details"
                          >
                            <User size={14} /> Lead details
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Desktop: show all buttons inline (hidden on mobile) */}
                  <div className="hidden md:flex items-center gap-1">
                  {/* Close/Reopen chat */}
                  {(selectedLead?.wati_status === "SOLVED") ? (
                    <button onClick={() => handleChatAction("open")} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-[11px] font-bold border border-green-200 hover:bg-green-100 transition-all" data-testid="header-open-chat">
                      <CheckCircle size={13} /> Reopen
                    </button>
                  ) : (
                    <button onClick={() => handleChatAction("close")} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-full text-[11px] font-bold border border-red-200 hover:bg-red-100 transition-all" data-testid="header-close-chat">
                      <XCircle size={13} /> Solved
                    </button>
                  )}
                  {/* Mark Read/Unread */}
                  {(selectedChat?.unread_count || 0) > 0 ? (
                    <button onClick={() => handleChatAction("mark-read")} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-[11px] font-bold border border-blue-200 hover:bg-blue-100 transition-all" data-testid="header-mark-read">
                      <MailOpen size={13} /> Mark Read
                    </button>
                  ) : (
                    <button onClick={() => handleChatAction("mark-unread")} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 rounded-full text-[11px] font-bold border border-orange-200 hover:bg-orange-100 transition-all" data-testid="header-mark-unread">
                      <MailCheck size={13} /> Unread
                    </button>
                  )}
                  {/* Label Picker */}
                  {/* Labels button removed — labels are in the right-side Info panel */}
                  <button onClick={() => { setShowPaymentModal(true); setGeneratedLink(null); setPaymentAmount(""); setPaymentDesc(""); setPaymentCurrency("INR"); setPayCustomerName(selectedLead?.customer_name || selectedLead?.name || ""); setPayCustomerPhone(selectedLead?.customer_phone || selectedLead?.phone || ""); setPayCustomerEmail(selectedLead?.customer_email || ""); setQrBase64(null); }} className="flex items-center gap-1.5 px-4 py-1.5 text-white rounded-full text-[11px] font-bold transition-all hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, #EA580C, #DC2626)", boxShadow: "0 2px 10px rgba(234,88,12,0.25)" }} title="Generate Payment Link" data-testid="payment-link-btn">
                    <IndianRupee size={13} /> Payment Link
                  </button>
                  <button onClick={() => {
                    // Prefill from recent messages
                    const lastCustomerMsg = [...(messages || [])].reverse().find((m) => m.direction === "incoming");
                    setTicketSubject(`WhatsApp chat with ${selectedLead?.name || selectedLead?.phone}`);
                    const recent = (messages || []).slice(-6).map((m) => `[${m.direction === "outgoing" ? "Us" : "Customer"}] ${m.content || "(media)"}`).join("\n");
                    setTicketDescription(lastCustomerMsg
                      ? `Escalated from WhatsApp. Recent conversation:\n\n${recent}\n\n--- Customer phone: +${selectedLead?.phone} ---`
                      : `Follow-up from WhatsApp chat with ${selectedLead?.name}. +${selectedLead?.phone}`);
                    setTicketInternal(true);
                    setTicketEmailOverride("help@exoticindia.com");
                    setShowCreateTicket(true);
                  }} className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-[11px] font-bold border border-amber-200 hover:bg-amber-100 transition-all" data-testid="escalate-to-email-btn" title="Create a Zoho email ticket from this chat">
                    <Mail size={12} /> Email Ticket
                  </button>
                  {/* Escalate button removed per user request (Apr 28 2026).
                      Escalated badge is preserved so already-escalated chats
                      still show their state. */}
                  {selectedLead?.is_escalated && (
                    <span className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold" data-testid="escalated-badge">
                      <AlertTriangle size={11} /> Escalated
                    </span>
                  )}
                  {/* Re-route button removed per user request (Apr 28 2026).
                      Department auto-routing still happens server-side; agents
                      can manually flip Sales/CS via the right-panel Department
                      buttons. */}
                  <button onClick={() => setShowNotifyTeamModal(true)} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-[11px] font-bold border border-indigo-200 hover:bg-indigo-100 transition-all" data-testid="notify-team-btn" title="Email this chat to Sales / CS / configured team">
                    <Send size={12} /> Notify team
                  </button>
                  {selectedLead?.ai_shared_payment && (
                    <span
                      className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-bold border border-emerald-200"
                      data-testid="ai-payment-badge"
                      title={`AI shared ${selectedLead.ai_shared_payment_type === "upi_qr" ? "UPI QR" : selectedLead.ai_shared_payment_type === "razorpay_link" ? "Razorpay link" : "payment"}${selectedLead.ai_shared_payment_amount ? ` of ₹${selectedLead.ai_shared_payment_amount}` : ""} at ${selectedLead.ai_shared_payment_at}`}
                    >
                      💳 AI shared payment
                    </span>
                  )}
                  {/* Detect Sentiment button removed per user request — sentiment
                      can still be inspected on the lead doc. */}
                  </div>
                  {/* Details toggle — visible everywhere (part of main action cluster on desktop, still accessible on mobile via kebab too) */}
                  <button onClick={() => setShowDetails(!showDetails)} className={`btn-icon hidden md:flex ${showDetails ? "bg-[--accent-pale] text-[--accent]" : ""}`} data-testid="toggle-details-btn" title="Details"><User size={16} /></button>
                </div>
              </div>
  );
}
